/**
 * SrBandPrimitive (R138) — "running band" rendering for the S/R indicator.
 *
 * Each band is a pair of stepped running lines — the most-recent Resistance
 * and most-recent Support at every bar — with a translucent fill between them,
 * forming the evolving S/R channel. Multiple bands (e.g. chart-TF + HTF) draw
 * together. Labels sit at the RIGHT end of each running line.
 *
 * Time units: UNIX seconds (IST-shifted, matching the chart's time axis).
 */
import type {
  ISeriesPrimitive,
  IPrimitivePaneView,
  IPrimitivePaneRenderer,
  SeriesAttachedParameter,
  Time,
  IChartApi,
  ISeriesApi,
  SeriesType,
} from 'lightweight-charts';
import type { CanvasRenderingTarget2D } from 'fancy-canvas';

export interface SrBandPoint { time: number; value: number; }

export interface SrBand {
  rPts: SrBandPoint[];
  sPts: SrBandPoint[];
  /** Fill colour (rgba, opacity baked in). */
  fillColor: string;
  /** Resistance / Support line colours (rgba, opacity baked in). */
  rColor: string;
  sColor: string;
  lineWidth: number;
  /** Right-end labels (empty string = no label). */
  rLabel: string;
  sLabel: string;
}

class SrBandRenderer implements IPrimitivePaneRenderer {
  constructor(
    private readonly bands: SrBand[],
    private readonly chart: IChartApi,
    private readonly series: ISeriesApi<SeriesType>,
  ) {}

  draw(target: CanvasRenderingTarget2D): void {
    if (!this.bands.length) return;
    const ts = this.chart.timeScale();
    const series = this.series;

    target.useBitmapCoordinateSpace(({ context: ctx, bitmapSize, horizontalPixelRatio: hpr, verticalPixelRatio: vpr }) => {
      ctx.save();
      const rightX = bitmapSize.width;
      const resolve = (pts: SrBandPoint[]): Array<{ x: number; y: number }> => {
        const out: Array<{ x: number; y: number }> = [];
        for (const p of pts) {
          const x = ts.timeToCoordinate(p.time as Time);
          if (x == null) continue;
          const y = series.priceToCoordinate(p.value);
          if (y == null) continue;
          out.push({ x: Number(x) * hpr, y: Number(y) * vpr });
        }
        return out;
      };
      const stroke = (coords: Array<{ x: number; y: number }>, color: string, w: number) => {
        if (coords.length < 2) return;
        ctx.lineWidth = Math.max(1, w * Math.min(hpr, vpr));
        ctx.strokeStyle = color;
        ctx.setLineDash([]);
        ctx.beginPath();
        ctx.moveTo(coords[0].x, coords[0].y);
        for (let i = 1; i < coords.length; i++) ctx.lineTo(coords[i].x, coords[i].y);
        ctx.stroke();
      };

      const fontPx = Math.round(10 * Math.min(hpr, vpr));
      for (const b of this.bands) {
        const rc = resolve(b.rPts);
        const sc = resolve(b.sPts);

        // Fill between the two running lines (where both exist).
        if (rc.length >= 2 && sc.length >= 2) {
          ctx.beginPath();
          ctx.moveTo(rc[0].x, rc[0].y);
          for (let i = 1; i < rc.length; i++) ctx.lineTo(rc[i].x, rc[i].y);
          for (let i = sc.length - 1; i >= 0; i--) ctx.lineTo(sc[i].x, sc[i].y);
          ctx.closePath();
          ctx.fillStyle = b.fillColor;
          ctx.fill();
        }

        stroke(rc, b.rColor, b.lineWidth);
        stroke(sc, b.sColor, b.lineWidth);

        // Right-end labels.
        ctx.font = `${fontPx}px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif`;
        ctx.textBaseline = 'bottom';
        if (b.rLabel && rc.length) {
          const last = rc[rc.length - 1];
          ctx.fillStyle = b.rColor;
          const tx = Math.max(2, Math.min(last.x, rightX - ctx.measureText(b.rLabel).width - 4 * hpr));
          ctx.fillText(b.rLabel, tx, last.y - 2 * vpr);
        }
        if (b.sLabel && sc.length) {
          const last = sc[sc.length - 1];
          ctx.fillStyle = b.sColor;
          ctx.textBaseline = 'top';
          const tx = Math.max(2, Math.min(last.x, rightX - ctx.measureText(b.sLabel).width - 4 * hpr));
          ctx.fillText(b.sLabel, tx, last.y + 2 * vpr);
          ctx.textBaseline = 'bottom';
        }
      }
      ctx.restore();
    });
  }
}

class SrBandPaneView implements IPrimitivePaneView {
  constructor(private readonly parent: SrBandPrimitive) {}
  zOrder() { return 'top' as const; }
  renderer(): IPrimitivePaneRenderer | null {
    const chart = this.parent.chart;
    const series = this.parent.series;
    if (!chart || !series) return null;
    return new SrBandRenderer(this.parent.bands, chart, series);
  }
}

export class SrBandPrimitive implements ISeriesPrimitive<Time> {
  bands: SrBand[] = [];
  chart: IChartApi | null = null;
  series: ISeriesApi<SeriesType> | null = null;
  private readonly _paneViews: SrBandPaneView[];

  constructor(bands: SrBand[]) {
    this.bands = bands;
    this._paneViews = [new SrBandPaneView(this)];
  }

  attached(params: SeriesAttachedParameter<Time>): void {
    this.chart = params.chart as IChartApi;
    this.series = params.series as ISeriesApi<SeriesType>;
  }
  detached(): void {
    this.chart = null;
    this.series = null;
  }

  updateAllViews(): void { /* renderer reads this.bands fresh each draw */ }
  paneViews(): readonly IPrimitivePaneView[] { return this._paneViews; }

  setData(bands: SrBand[]): void {
    this.bands = bands;
    this.chart?.applyOptions({});
  }
}
